// /**
//  * Partners - list
//  */

// React
import React, {useContext} from 'react';
import {Link} from 'react-router-dom';
import HeaderBuilder from '../../../utils/headerBuilder';
// Context
import AuthContext from '../../../authContext';

// UI
import theme from '../../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiClose,
  mdiFileDocumentOutline,
  mdiPencilOutline,
  mdiCheckDecagram,
  mdiCalendarSearch,
  mdiMapMarkerMultipleOutline,
  mdiCheck,
} from '@mdi/js';

// Components
import Can from '../../../components/Can';

// Helper
import {formaterPhoneNumber} from '../../../utils/helpers';

// Type
import {Partner} from '../interfaces';

type Data = Partner;
type Props = {
  data: any;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpenConfirmActive: (id: number) => void;
  handleOpen: (id: number, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    body: {
      fontSize: 14,
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const headCells: HeadCell[] = [
  {
    id: 'name',
    label: 'نام شرکت',
  },
  {id: 'id', label: 'شناسه شرکت'},
  {id: 'phoneNumber', label: 'شماره تماس شرکت'},
  {id: 'workField', label: 'حوزه فعالیت'},
  {id: 'ranking', label: 'تعداد مراکز توزیع'},
  {id: 'adminId', label: 'کاربر مرکز'},
];

function EnhancedTableHead(props: EnhancedTableProps) {
  const {order, orderBy, onRequestSort} = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [dense] = React.useState(false);
  const {handleOpenConfirmDisable, handleOpenConfirmActive, handleOpen} = props;

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <HeaderBuilder
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
            filterVal={props.filterVal}
            onFilterValue={(filtervalues: any) => {
              props.onFilterValue(filtervalues);
            }}
            head={props.head}
            removeFilterHandler={props.removeFilterHandler}
            haveAction={true}
            filterLabelTranslator={props.filterLabelTranslator}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow key={index}>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.name}
                    </TableCell>
                    <TableCell align="center">{row.id}</TableCell>
                    <TableCell align="center">
                      {formaterPhoneNumber(row.phoneNumber)}
                    </TableCell>
                    <TableCell align="center">{row.workField}</TableCell>
                    <TableCell align="center">{row.centersCount}</TableCell>
                    <TableCell align="center">{row.adminId}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        title="جزئیات قرارداد شرکت"
                        onClick={() => handleOpen(row.id, 'detail')}
                      >
                        <Icon
                          path={mdiFileDocumentOutline}
                          size={1}
                          color={theme.palette.green.main}
                        />
                      </IconButton>
                      <Link to={`/centers/${row.id}`}>
                        <IconButton
                          title="لیست مراکز"
                          size="small"
                          onClick={() => handleOpenConfirmDisable(row.id)}
                        >
                          <Icon path={mdiMapMarkerMultipleOutline} size={1} />
                        </IconButton>
                      </Link>
                      <Link to={`/products/${row.id}`}>
                        <IconButton
                          title="لیست محصولات"
                          size="small"
                          onClick={() => handleOpenConfirmDisable(row.id)}
                        >
                          <Icon path={mdiCheckDecagram} size={1} />
                        </IconButton>
                      </Link>

                      <Can
                        role={authContext.user.role}
                        perform="partners:editDetails"
                        yes={() => (
                          <IconButton
                            title="ویرایش"
                            size="small"
                            onClick={() => handleOpen(row.id, 'edit')}
                          >
                            <Icon
                              path={mdiPencilOutline}
                              size={1}
                              color={theme.palette.blue.main}
                            />
                          </IconButton>
                        )}
                      />

                      <Can
                        role={authContext.user.role}
                        perform="user:disableUser"
                        yes={() => (
                          <>
                            {row.status === 'SUSPENDED' ||
                            row.status === 'REGISTERED' ? (
                              <IconButton
                                aria-label="cancele"
                                title=" فعال کردن شرکت "
                                size="small"
                                onClick={() => handleOpenConfirmActive(row.id)}
                              >
                                <Icon
                                  path={mdiCheck}
                                  size={1}
                                  color={theme.palette.green.main}
                                />
                              </IconButton>
                            ) : (
                              <IconButton
                                aria-label="cancele"
                                title="غیر فعال کردن شرکت "
                                size="small"
                                onClick={() => handleOpenConfirmDisable(row.id)}
                              >
                                <Icon
                                  path={mdiClose}
                                  size={1}
                                  color={theme.palette.error.main}
                                />
                              </IconButton>
                            )}
                          </>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
